import {
  IBodyDetails, IHeartRate, ILiveMetric, IMetricResult,
} from 'fitbeat.models';
import { CardioOrResistance, UserProfile, UserWorkoutSession } from '../../entities';
import { QueuedPromiseRequests } from '../../helpers';
import { calculateLatesMetrics } from '../../metrics';
import {
  IUserProfileRepository,
  IWorkoutHeartRateDataRepository,
  IWorkoutRepository,
} from '../../persistence';
import { getTimeInWorkout } from '../sessionsequence';
import { InMemoryMetricsCache } from './metrics-cache';
import { HeartRatePacket } from './metrics-receiver';

export interface IPerformanceMetric {
  heartRate?: number;
  timestamp: Date;
  calories: number;
  beltNumber: string;
  meps: number;
  effort: number;
}
export interface IProcessedMetricResults {
  beltNumber: string;
  metricResult: IMetricResult;
}

interface IWorkoutToProcess {
  userProfileId: string;
  workoutId: string;
  endTime: Date;
  latestPerformanceMetrics: IMetricResult;
}

interface IRecentlySeenDebugInfo {
  lastSeen: Date;
  hrDataCount: number;
}

interface IMetricsLoggingDetails {
  uid: string;
  beltNumber?: string;
}

const logPrefix = 'PerformanceMetrics';

export type OnMetricResult = (uid: string, metricResult: IMetricResult | ILiveMetric) => Promise<void>;

export type CalculateAndPublishMetricsStatus =
  | 'ActiveWorkoutNotFound'
  | 'MetricsProcessedAndPublished'
  | 'MetricsNotProcessed';

export type CalculateMetricsFunction = (
  bodyDetails: IBodyDetails,
  heartRatePoints: IHeartRate[],
  cardioOrResistance: CardioOrResistance,
) => IMetricResult | undefined;
export class PerformanceMetrics {
  public static sanatizeHeartRateIdentifier(heartRateIdentifier: string) {
    return heartRateIdentifier.replace(/ /g, '-');
  }

  public static formatBeltIdentifierForQuery(beltId: string): string {
    return beltId.replace(/\D/g, '').slice(-7).replace(/^0+/, '');
  }

  private userProfileWorkoutProcessingQueue: Map<string, IWorkoutToProcess> = new Map<string, IWorkoutToProcess>();

  private usersRecentlySeen: Map<string, Date> = new Map<string, Date>();

  private onProcessedMetric: OnMetricResult;

  constructor(
    private workoutRepository: IWorkoutRepository,
    private userRepository: IUserProfileRepository,
    private metricsCache: InMemoryMetricsCache,
    private calculateMetricsFunction: CalculateMetricsFunction,
    private workoutHeartRateDataRepository: IWorkoutHeartRateDataRepository,
    private logger: any,
  ) { }

  public async processHeartRatePacket(
    heartRatePacket: HeartRatePacket,
    timestamp: Date,
    now = Date.now(),
  ) {
    const beltNumber = heartRatePacket.deviceNumber;
    const heartRate: IHeartRate = {
      heartRate: heartRatePacket.heartRate,
      timestamp,
    };

    const userProfileFetchTimer = this.logger.startTimer();
    const formattedBeltNumber = PerformanceMetrics.formatBeltIdentifierForQuery(beltNumber);
    const userProfiles = await QueuedPromiseRequests.queue(
      async () => this.userRepository.getByStudioBeltNumber(formattedBeltNumber),
      `hr-getByStudioBeltNumber-${formattedBeltNumber}`,
    );
    userProfileFetchTimer.done({ message: `${logPrefix} fetched users for beltNumber: ${beltNumber}` });

    if (userProfiles.length === 0) {
      this.logger.warn(`${logPrefix} no users found for belt ${beltNumber}`);
      return;
    }
    if (userProfiles.length > 1) {
      this.logger.warn(`${logPrefix} multiple users ${userProfiles.length} found for belt ${beltNumber}`);
    }

    const processAll = userProfiles.map(
      (userProfile: UserProfile) => this.processHeartRate(userProfile, heartRate, beltNumber, now),
    );

    await Promise.all(processAll);
  }

  public async processHeartRate(
    userProfile: UserProfile,
    heartRate: IHeartRate,
    beltNumber?: string,
    now = Date.now(),
  ) {
    const { timestamp } = heartRate;

    this.usersRecentlySeen.set(userProfile.uid, timestamp);
    this.handleLiveMetrics(userProfile, heartRate, {
      uid: userProfile.uid,
      beltNumber,
    });
    return QueuedPromiseRequests.queue(async () => this.calculateAndPublishMetricsForBelt(
      userProfile,
      heartRate,
      now,
    ), `hr-calculateAndPublish-${userProfile.uid}`);
  }

  public addOnMetricListener(onProcessedMetric: OnMetricResult) {
    this.onProcessedMetric = onProcessedMetric;
  }

  public resetCaches() {
    this.metricsCache.reset();
  }

  public async processMetricsForEndTime(endTime: Date): Promise<void> {
    try {
      for (const [_, workoutToProcess] of this.userProfileWorkoutProcessingQueue) {
        const workoutHasEnded = workoutToProcess.endTime <= endTime;
        if (!workoutHasEnded) {
          continue;
        }
        await this.processUserWorkout(workoutToProcess);
      }
    } catch (e) {
      this.logger.error(`${logPrefix} failed to process metrics for workouts ending on: ${endTime}: ${e.message || e}`);
    }
  }

  public hasUserRecentlyReceivedHeartRateData(uid: string, now: Date): boolean {
    this.cleanRecentlySeenUsersList(now);
    return this.usersRecentlySeen.has(uid);
  }

  public getRecentlySeenUsers(now: Date): string[] {
    this.logger.info(`${logPrefix} fetching recently seen users for: ${now.toISOString()}`);
    this.cleanRecentlySeenUsersList(now);
    return Array.from(this.usersRecentlySeen.keys());
  }

  public getRecentUserHeartRateInfo(): Map<string, IRecentlySeenDebugInfo> {
    const result = new Map<string, IRecentlySeenDebugInfo>();
    for (const [userId, lastSeen] of this.usersRecentlySeen) {
      const heartRateDataCount = this.metricsCache.get(userId).length;
      result[userId] = {
        lastSeen,
        heartRateDataCount,
      };
    }
    return result;
  }

  private cleanRecentlySeenUsersList(now: Date) {
    const timeToConsiderHrAsRecentInMs = 60000;
    for (const [userId, lastSeen] of this.usersRecentlySeen) {
      const seenDifference = now.getTime() - lastSeen.getTime();
      const recentlySeen = seenDifference <= timeToConsiderHrAsRecentInMs;
      if (!recentlySeen) {
        this.usersRecentlySeen.delete(userId);
      }
    }
  }

  private async processUserWorkout(workoutToProcess: IWorkoutToProcess): Promise<void> {
    try {
      this.logger.info(
        `${logPrefix} processing user workout: ${JSON.stringify({
          workoutId: workoutToProcess.workoutId,
          endTime: workoutToProcess.endTime,
          userId: workoutToProcess.userProfileId,
        })}`,
      );

      const latestUserWorkout = await this.workoutRepository.getWorkoutSessionFromId(
        workoutToProcess.workoutId,
      );
      if (!latestUserWorkout) {
        throw new Error(`failed to load workout ${workoutToProcess.workoutId}`);
      }
      latestUserWorkout.performanceMetrics = workoutToProcess.latestPerformanceMetrics;

      UserWorkoutSession.markForCompletionReprocessing(latestUserWorkout);

      await this.workoutRepository.update(latestUserWorkout);
      this.saveMetricsToDb(latestUserWorkout)
        .catch((e) => this.logger.warn(`${logPrefix} saveMetricsToDb for failed (in background): ${JSON.stringify({
          e: e.message || e,
          workoutUniqueReference: latestUserWorkout.uniqueReference,
        })}`));
    } finally {
      this.userProfileWorkoutProcessingQueue.delete(workoutToProcess.userProfileId);
      this.metricsCache.clear(workoutToProcess.userProfileId);
    }
  }

  private async calculateAndPublishMetricsForBelt(
    userProfile: UserProfile,
    heartRate: IHeartRate,
    now: number,
  ): Promise<CalculateAndPublishMetricsStatus> {
    const { uid } = userProfile;
    const shouldProcessMetrics = this.metricsCache.add(uid, heartRate);

    if (!shouldProcessMetrics) {
      return 'MetricsNotProcessed';
    }

    const userWorkout = await this.workoutRepository.getWorkoutsCurrentlyActiveInStudio(
      userProfile.uid,
      heartRate.timestamp,
    );

    if (!userWorkout) {
      this.metricsCache.clear(uid);
      return 'ActiveWorkoutNotFound';
    }

    if (new Date(userWorkout.endTime).getTime() <= now) {
      this.logger.info(`${logPrefix} ignoring heart rate that was received after workout ended ${JSON.stringify({
        heartRatePacketInfo: heartRate,
        workoutId: userWorkout.uniqueReference,
        workoutEndTime: userWorkout.endTime,
      })}`);
      return 'MetricsNotProcessed';
    }

    const allDataPointsForBelt = this.metricsCache.get(uid);
    const calculatedResults = this.calculateMetricsFunction(
      userProfile,
      allDataPointsForBelt,
      userWorkout.settings.cardioOrResistance,
    );
    if (!calculatedResults) {
      return 'MetricsNotProcessed';
    }
    this.logger.info(`${logPrefix} calcualted metrics for ${uid}`, calculatedResults);
    this.userProfileWorkoutProcessingQueue.set(userProfile.uid, {
      endTime: userWorkout.endTime,
      workoutId: userWorkout.uniqueReference,
      latestPerformanceMetrics: calculatedResults,
      userProfileId: userProfile.uid,
    });
    this.publishNewMetrics(uid, calculatedResults)
      .catch((e) => this.logger.warn(`${logPrefix} publishNewMetrics failed (in background)`, {
        e,
        uid,
      }));
    return 'MetricsProcessedAndPublished';
  }

  private async saveMetricsToDb(workout: UserWorkoutSession) {
    try {
      const currentMetricsForBelt = this.metricsCache.get(workout.userProfileId);
      const mappedMetricsForBelt: IHeartRate[] = currentMetricsForBelt.map((heartRate: IHeartRate) => ({
        ...heartRate,
        timestamp: getTimeInWorkout({
          endTime: workout.endTime,
          workoutDurationInMs: UserWorkoutSession.getWorkoutDurationInMs(workout),
        }, heartRate.timestamp),
      }));
      await this.workoutHeartRateDataRepository.add(workout.uniqueReference, mappedMetricsForBelt);
    } catch (e) {
      this.logger.error(`${logPrefix} Failed to save raw metric data for ${workout.uniqueReference}: ${e.message}`);
    }
  }

  private handleLiveMetrics(userProfile: UserProfile, heartRate: IHeartRate, logDetails: IMetricsLoggingDetails) {
    if (!this.onProcessedMetric) {
      return;
    }
    const liveMetricsResult = calculateLatesMetrics(heartRate, userProfile);
    const previousCalculatedMetrics = this.userProfileWorkoutProcessingQueue.get(userProfile.uid);
    this.onProcessedMetric(userProfile.uid, {
      ...previousCalculatedMetrics?.latestPerformanceMetrics,
      ...liveMetricsResult,
    }).catch((e) => this.logger.warn(`${logPrefix} onProcessedMetric failed (in background) ${JSON.stringify({
      e,
      ...logDetails,
    })}`));
  }

  private async publishNewMetrics(uid: string, metrics: IMetricResult) {
    if (!this.onProcessedMetric) {
      return;
    }
    await this.onProcessedMetric(uid, metrics);
  }
}
